import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { departmentService } from '../../services/departmentService';
import Modal from '../ui/Modal';
import Select from '../ui/Select';
import Button from '../ui/Button';
import { 
  SECTORS,
  USER_ROLES,
  isNonDepartmentRole,
  USER_STATUS,
  ROLE_LABELS,
  getSectorLabel,
  getDepartmentLabel
} from '../../constants/authConstants';

const ApproveUserModal = ({ isOpen, onClose, user, onApprove }) => {
  const { isMobile, getResponsiveValue } = useTheme();
  
  const [formData, setFormData] = useState({
    role: USER_ROLES.DEPARTMENT,
    sector: '',
    department: ''
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  const gridCols = getResponsiveValue({
    mobile: 'grid-cols-1',
    desktop: 'grid-cols-2'
  });

  const gridGap = getResponsiveValue({
    mobile: 'gap-3',
    desktop: 'gap-4'
  });

  const containerPadding = getResponsiveValue({
    mobile: 'p-4',
    desktop: 'p-6'
  });

  const buttonSpacing = getResponsiveValue({
    mobile: 'flex-col space-y-3',
    desktop: 'flex-row space-x-3'
  });

  // Initialize form data when modal opens with user data
  useEffect(() => {
    if (isOpen && user) {
      setFormData({
        role: user.role || USER_ROLES.DEPARTMENT,
        sector: user.sector || '',
        department: user.department || ''
      });
      loadDepartments();
    }
  }, [isOpen, user]);

  // Filter departments based on selected sector
  useEffect(() => {
    if (formData.sector) {
      const depts = departments.filter(d => 
        d.sector === formData.sector && d.isActive
      );
      setFilteredDepartments(depts);
    } else {
      setFilteredDepartments([]);
    }
  }, [formData.sector, departments]);

  const loadDepartments = async () => {
    try {
      setLoadingDepartments(true);
      const allDepartments = await departmentService.getActiveDepartments();
      setDepartments(allDepartments);
    } catch (error) {
      console.error('Error loading departments:', error);
      setDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Clear department when sector changes
    if (field === 'sector') {
      setFormData(prev => ({
        ...prev,
        department: ''
      }));
    }

    // Clear sector/department for Admin and Planning Official roles
    if (field === 'role' && isNonDepartmentRole(value)) {
      setFormData(prev => ({
        ...prev,
        sector: '',
        department: ''
      }));
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.sector;
        delete newErrors.department;
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Only validate sector/department for Department users
    if (formData.role === USER_ROLES.DEPARTMENT) {
      if (!formData.sector) {
        newErrors.sector = 'Sector is required for department users';
      }
      
      if (!formData.department) {
        newErrors.department = 'Department is required for department users';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleApprove = async () => {
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const approvalData = {
        role: formData.role,
        status: USER_STATUS.ACTIVE
      };

      // Only add sector/department for Department users
      if (formData.role === USER_ROLES.DEPARTMENT) {
        approvalData.sector = formData.sector;
        approvalData.department = formData.department;
      } else {
        // Clear sector/department for non-department roles
        approvalData.sector = null;
        approvalData.department = null;
      }
      
      await onApprove(user, approvalData);
      
      handleClose();
    } catch (error) {
      console.error('Error approving user:', error);
      setErrors({ general: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        role: USER_ROLES.DEPARTMENT,
        sector: '',
        department: ''
      });
      setErrors({});
      onClose();
    }
  };

  const getDepartmentOptions = () => {
    if (isNonDepartmentRole(formData.role)) {
      return [{ value: '', label: 'Not applicable for this role' }];
    }

    if (!formData.sector) {
      return [{ value: '', label: 'Select a sector first' }];
    }
    
    if (loadingDepartments) {
      return [{ value: '', label: 'Loading departments...' }];
    }
    
    if (filteredDepartments.length === 0) {
      return [{ value: '', label: 'No departments available' }];
    }
    
    return filteredDepartments
      .sort((a, b) => (a.priority || 999) - (b.priority || 999))
      .map(dept => ({
        value: dept.departmentCode,
        label: dept.departmentName
      }));
  };

  const isNonDepartmentUser = isNonDepartmentRole(formData.role);

  // Get the department name for display
  const getOriginalDepartmentName = () => {
    if (!user?.department || !departments.length) return user?.department;
    const dept = departments.find(d => d.departmentCode === user.department);
    return dept ? dept.departmentName : user.department;
  };

  if (!user) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Approve User - ${user.fullName}`}
      size="lg"
      maxHeight="large"
      closeOnOverlay={!loading}
    >
      <div className={containerPadding}>
        {errors.general && (
          <div className="mb-4 bg-error-subtle border border-border-error rounded-xl p-3">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-error mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-sm text-error">{errors.general}</span>
            </div>
          </div>
        )}

        <div className="space-y-6">
          {/* User Information Display */}
          <div className="bg-surface-secondary rounded-xl p-4">
            <h4 className="text-sm font-semibold text-text-primary mb-3">User Information</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center">
                <span className="text-text-tertiary w-24">Name:</span>
                <span className="text-text-primary font-medium">{user.fullName}</span>
              </div>
              <div className="flex items-center">
                <span className="text-text-tertiary w-24">Email:</span>
                <span className="text-text-primary">{user.email}</span>
              </div>
            </div>
          </div>

          {/* Role Selection */}
          <div className="space-y-4">
            <div className="border-t border-border-secondary pt-4">
              <h4 className="text-sm font-semibold text-text-primary mb-3">Assign Role & Access</h4>
            </div>

            <Select
              label="User Role"
              value={formData.role}
              onChange={(e) => handleInputChange('role', e.target.value)}
              options={[
                { value: USER_ROLES.DEPARTMENT, label: ROLE_LABELS[USER_ROLES.DEPARTMENT] },
                { value: USER_ROLES.PLANNING_OFFICIAL, label: ROLE_LABELS[USER_ROLES.PLANNING_OFFICIAL] },
                { value: USER_ROLES.DONER, label: ROLE_LABELS[USER_ROLES.DONER] },
                { value: USER_ROLES.ADMIN, label: ROLE_LABELS[USER_ROLES.ADMIN] }
              ]}
              required
              disabled={loading}
              className="text-sm"
            />

            {isNonDepartmentUser && (
              <div className="bg-info-subtle border border-border-primary rounded-xl p-3">
                <div className="flex items-start">
                  <svg className="w-4 h-4 text-info mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="text-sm text-info">
                    <p className="font-medium mb-1">
                      {ROLE_LABELS[formData.role]} Access
                    </p>
                    <p className="text-xs">
                      {formData.role === USER_ROLES.ADMIN 
                        ? 'Full system access with user management capabilities.' 
                        : 'View-only access to user management with full planning access.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {formData.role === USER_ROLES.DEPARTMENT && (
              <div className={`grid ${gridCols} ${gridGap}`}>
                <Select
                  label="Sector"
                  value={formData.sector}
                  onChange={(e) => handleInputChange('sector', e.target.value)}
                  options={[
                    { value: '', label: 'Select a sector' },
                    ...SECTORS
                  ]}
                  error={errors.sector}
                  required
                  disabled={loading}
                  className="text-sm"
                />

                <Select
                  label="Department"
                  value={formData.department}
                  onChange={(e) => handleInputChange('department', e.target.value)}
                  options={getDepartmentOptions()}
                  error={errors.department}
                  required
                  disabled={!formData.sector || loading || loadingDepartments}
                  className="text-sm"
                />
              </div>
            )}
          </div>

          {/* Originally Requested Information */}
          <div className="bg-surface-secondary border border-border-primary rounded-xl p-4">
            <h4 className="text-sm font-semibold text-text-primary mb-3 flex items-center">
              <svg className="w-4 h-4 mr-2 text-text-tertiary" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              Originally Requested
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center">
                <span className="text-text-tertiary w-32">Role:</span>
                <span className="text-text-primary">{ROLE_LABELS[user.role] || user.role}</span>
              </div>
              {user.sector && (
                <div className="flex items-center">
                  <span className="text-text-tertiary w-32">Sector:</span>
                  <span className="text-text-primary">{getSectorLabel(user.sector)}</span>
                </div>
              )}
              {user.department && (
                <div className="flex items-center">
                  <span className="text-text-tertiary w-32">Department:</span>
                  <span className="text-text-primary">{getOriginalDepartmentName()}</span>
                </div>
              )}
              {!user.sector && !user.department && (
                <div className="text-text-tertiary italic text-xs">No sector or department requested</div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className={`flex ${buttonSpacing} pt-4 border-t border-border-primary`}>
            <Button
              type="button"
              onClick={handleClose}
              variant="outline"
              disabled={loading}
              className={getResponsiveValue({ mobile: 'w-full', desktop: 'flex-1' })}
            >
              Cancel
            </Button>
            
            <Button
              type="button"
              onClick={handleApprove}
              loading={loading}
              disabled={loading || loadingDepartments}
              className={getResponsiveValue({ mobile: 'w-full', desktop: 'flex-1' })}
            >
              {loading ? 'Approving...' : 'Approve User'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ApproveUserModal;